import { makeAutoObservable } from "mobx";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";
import { getApi, getApiError, isSessionExpired } from "../../../api";
import { ViewEntityStore } from "../../common/store/types";
import { userWidgetConfigService } from "../services/userWidgetConfigService";
import { userWidgetConfigDisplayId } from "./UserWidgetConfigCommonStore";
import { UserWidgetConfig } from "../../../api/graphql";
import { userDisplayId } from "../../user/store/UserCommonStore";
import { dashboardWidgetDisplayId } from "../../dashboardWidget/store/DashboardWidgetCommonStore";
import { userService } from "../../user/services/userService";
import { dashboardWidgetService } from "../../dashboardWidget/services/dashboardWidgetService";
import { InitializeParams } from "../../../store/types";

export class UserWidgetConfigViewStore implements ViewEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  service = userWidgetConfigService;
  userWidgetConfigInstance: UserWidgetConfig | null = null;
  cardState: Record<string, boolean> = {};
  savedId: string | null = null;
  cancelled = false;
  isDrawerMode = false;
  isModalMode = false;
  callbackParams: CallbackDataParams | null = null;
  idValue: string | undefined;
  userService = userService;
  userSearchValue: string = "";
  userIdValue: string = "";
  userSelectedValue: { displayId: string; id: string } | null = null;
  widgetService = dashboardWidgetService;
  widgetSearchValue: string = "";
  widgetIdValue: string = "";
  widgetSelectedValue: { displayId: string; id: string } | null = null;
  enabledValue: boolean = false;
  positionValue: string = "{}";
  configOverrideValue: string = "{}";
  displayOrderValue: number | undefined = undefined;

  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;

    makeAutoObservable(this);
  }

  destroy() {
    // this object will be unusable
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  toggleCardState(id: string) {
    this.cardState[id] = !this.cardState[id];
  }

  setCardState(id: string, status: boolean) {
    this.cardState[id] = status;
  }

  getCardState(id: string) {
    if (!(id in this.cardState)) {
      this.cardState[id] = true;
    }
    return !!this.cardState[id];
  }

  *loadUserWidgetConfigById(id: string): any {
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.userWidgetConfigOne({ id });
    this.userWidgetConfigInstance = result.userWidgetConfigOne;
    if (this.userWidgetConfigInstance) {
      this.loadValues(this.userWidgetConfigInstance);
    }
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get displayId(): string {
    if (this.userWidgetConfigInstance) {
      return userWidgetConfigDisplayId(
        this.userWidgetConfigInstance,
        this.langStore
      );
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.UserWidgetConfigSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  setContentTop(_contentTop: number) {}

  *returnedData(params: CallbackDataParams): any {
    if (
      (!params.storeId || params.storeId === this.storeId) &&
      params.returnData &&
      !params.returnData.cancelled &&
      params.returnData.resultId
    ) {
      if (params.callerData?.fieldName === "user") {
        const api = yield* getApi(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.userOne({ id: params.returnData.resultId });
        const user = result.userOne;
        this.userSelectedValue = user;
      } else if (params.callerData?.fieldName === "widget") {
        const api = yield* getApi(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.dashboardWidgetOne({
          id: params.returnData.resultId,
        });
        const dashboardWidget = result.dashboardWidgetOne;
        this.widgetSelectedValue = dashboardWidget;
      }
    }
  }

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  get userValue() {
    if (this.userSelectedValue) {
      return {
        id: this.userSelectedValue.id,
        displayId: userDisplayId(this.userSelectedValue, this.langStore),
      };
    }
    return null;
  }

  get widgetValue() {
    if (this.widgetSelectedValue) {
      return {
        id: this.widgetSelectedValue.id,
        displayId: dashboardWidgetDisplayId(
          this.widgetSelectedValue,
          this.langStore
        ),
      };
    }
    return null;
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.userWidgetConfigInstance = null;
    this.userSearchValue = "";
    this.userIdValue = "";
    this.widgetSearchValue = "";
    this.widgetIdValue = "";
    this.enabledValue = false;
    this.positionValue = "{}";
    this.configOverrideValue = "{}";
    this.displayOrderValue = undefined;
  }

  close() {
    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
    } else {
      this.rootStore.router.goBack();
    }
  }

  cancelUserWidgetConfig() {
    this.cancelled = true;
    this.close();
  }

  get isEditEnabled(): boolean {
    return this.rootStore.authStore.hasPermissions("userWidgetConfig_update");
  }

  editUserWidgetConfig() {
    const id = this.userWidgetConfigInstance?.id;
    if (!id) return;

    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      this.rootStore.closeModalView();
    }
    this.rootStore.router.goTo(
      "/userWidgetConfig/update/" + id,
      undefined,
      undefined,
      undefined,
      this.breadcrumbLabel
    );
  }

  printUserWidgetConfig(handlePrint: () => void): void {
    handlePrint();
  }

  *closeUserWidgetConfig(): any {
    yield this.close();
  }

  loadValues(instance: UserWidgetConfig) {
    this.idValue = instance.id;
    this.userSelectedValue = instance.user;
    this.widgetSelectedValue = instance.widget;
    this.enabledValue = instance.enabled;
    this.positionValue = instance.position;
    this.configOverrideValue = instance.configOverride || "";
    this.displayOrderValue = instance.displayOrder || undefined;
  }

  getState(): InitializeParams {
    return {
      id: this.idValue,
    };
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const id = params?.id;

      if (id) {
        yield this.loadUserWidgetConfigById(id);
      }

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
